import { useMutation } from '@apollo/client';

import { PaymentCardType } from 'types/creditCard';

import { refetchOngoingPaymentCardRequestQuery } from '../../../../../../hooks/useOngoingPaymentCardsRequests';
import { useRequestNewCardContext } from '../../../../hooks/useRequestNewCardContext';
import { SubmitPhysicalPaymentCardOrderRequestDocument } from './submitPhysicalPaymentCardOrderRequest.gql';
import { SubmitVirtualBudgetCardOrderRequestDocument } from './submitVirtualBudgetCardOrderRequest.gql';
import { SubmitVirtualPaymentCardOrderRequestDocument } from './submitVirtualPaymentCardOrderRequest.gql';

const useSubmitPaymentCardRequest = () => {
  const { sharedState } = useRequestNewCardContext();
  const {
    bankAccountId,
    budgetCardAmount,
    budgetCardDescription,
    cardType,
    companyProfileId,
    isBudgetCard,
    pinCode,
  } = sharedState;

  const [
    submitPhysicalPaymentCardOrderRequest,
    submitPhysicalPaymentCardOrderRequestHook,
  ] = useMutation(SubmitPhysicalPaymentCardOrderRequestDocument);

  const [
    submitVirtualPaymentCardOrderRequest,
    submitVirtualPaymentCardOrderRequestHook,
  ] = useMutation(SubmitVirtualPaymentCardOrderRequestDocument);

  const [
    submitVirtualBudgetCardOrderRequest,
    submitVirtualBudgetCardOrderRequestHook,
  ] = useMutation(SubmitVirtualBudgetCardOrderRequestDocument);

  const execMutation = () => {
    if (cardType === PaymentCardType.PHYSICAL) {
      return submitPhysicalPaymentCardOrderRequest({
        awaitRefetchQueries: true,
        refetchQueries: [
          refetchOngoingPaymentCardRequestQuery(companyProfileId),
        ],
        variables: { input: { bankAccountId, companyProfileId, pin: pinCode } },
      });
    }

    if (cardType === PaymentCardType.VIRTUAL) {
      if (isBudgetCard) {
        return submitVirtualBudgetCardOrderRequest({
          awaitRefetchQueries: true,
          refetchQueries: [
            refetchOngoingPaymentCardRequestQuery(companyProfileId),
          ],
          variables: {
            input: {
              bankAccountId,
              companyProfileId,
              description: budgetCardDescription,
              limitPaymentAll: budgetCardAmount,
            },
          },
        });
      }

      return submitVirtualPaymentCardOrderRequest({
        awaitRefetchQueries: true,
        refetchQueries: [
          refetchOngoingPaymentCardRequestQuery(companyProfileId),
        ],
        variables: { input: { bankAccountId, companyProfileId } },
      });
    }

    throw new Error('[usePaymentCardRequest] missing "cardType"');
  };

  const submitVirtualOrderRequestHook = isBudgetCard
    ? submitVirtualBudgetCardOrderRequestHook
    : submitVirtualPaymentCardOrderRequestHook;

  return {
    submitPaymentCardRequest: execMutation,
    ...(cardType === PaymentCardType.PHYSICAL
      ? submitPhysicalPaymentCardOrderRequestHook
      : submitVirtualOrderRequestHook),
  };
};

export default useSubmitPaymentCardRequest;
